import React, { useEffect, useId, useLayoutEffect, useRef } from 'react';
import { toast } from 'sonner';
import { ArrowUp, Square, Mic } from '@/shared/ui/icons';
import { Button } from '../../shared/ui/button';
import { QuantisAISpeechActivity } from '../../shared-ui/quantis-ai/QuantisAISpeechActivity';
import { QuantisAIComposerFrame, QUANTIS_AI_COMPOSER_METRICS } from '../../shared-ui/quantis-ai/QuantisAIComposerFrame';
import { SIComposerTrailingControls, type TakeoffModelPicker } from './SIComposerControls';
import { useSIWebSpeech } from './useSIWebSpeech';
import type { SIModelPicker } from './siModels';
import type { WorkspaceBot } from '../../services/WorkspaceBotsService';
import { SIBotMentionMenu, useBotMention } from './SIBotMentionMenu';

export interface SIMessageComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onStop?: () => void;
  onSteer?: () => void;
  queueWhileRunning?: boolean;
  running?: boolean;
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLTextAreaElement | null>;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  actions?: React.ReactNode;
  assistantName?: string;
  placeholder?: string;
  maxLength?: number;
  sendAriaLabel?: string;
  composerAriaLabel?: string;
  /** Omitted where the model is fixed, as in a drawing takeoff. */
  modelPicker?: SIModelPicker;
  takeoffModelPicker?: TakeoffModelPicker;
  /** Bots offered when the person types @. Omit where bots cannot be reached. */
  mentionBots?: WorkspaceBot[];
  speechContextKey?: string;
  latestVoiceReply?: {id: string; text: string};
  onVoiceSubmit?: (text: string) => void | Promise<void>;
  speechFailed?: boolean;
}

const noBots: WorkspaceBot[] = [];

function resizeComposerInput(input: HTMLTextAreaElement) {
  input.style.height = 'auto';
  input.style.height = `${Math.min(QUANTIS_AI_COMPOSER_METRICS.inputMaxHeight, Math.max(QUANTIS_AI_COMPOSER_METRICS.inputMinHeight, input.scrollHeight))}px`;
  input.style.overflowY = input.scrollHeight > QUANTIS_AI_COMPOSER_METRICS.inputMaxHeight ? 'auto' : 'hidden';
}

/** The same responsive QuantisAI composer serves the canvas, panel and drawing tools. */
export function SIMessageComposer({ value, onChange, onSend, onStop, onSteer, queueWhileRunning = false, running = false,
  disabled = false, inputRef, onKeyDown, actions, assistantName = 'QuantisAI',
  placeholder, maxLength, sendAriaLabel, composerAriaLabel, modelPicker, takeoffModelPicker,
  mentionBots = noBots, speechContextKey = assistantName, latestVoiceReply, onVoiceSubmit, speechFailed = false }: SIMessageComposerProps) {
  const speech = useSIWebSpeech({contextKey: speechContextKey, draft: value, onDraftChange: onChange,
    disabled, voiceDisabled: !onVoiceSubmit, pending: running, latestReply: latestVoiceReply, maxLength: maxLength ?? 4000,
    onVoiceSubmit: onVoiceSubmit ?? (() => {}), failed: speechFailed});
  const speechToastId = useId();
  const shownSpeechError = useRef<string | null>(null);
  useEffect(() => {
    if (!speech.error) { shownSpeechError.current = null; return; }
    if (shownSpeechError.current === speech.error) return;
    shownSpeechError.current = speech.error;
    toast.error(speech.error, {id: speechToastId, duration: 6000,
      ...(speech.transcript && !value.trim() ? {action: {label: 'Use transcript', onClick: () => {if (composerRef.current && !composerRef.current.value.trim()) {speech.stop(); onChange(speech.transcript);}}}} : {})});
  }, [speech.error, speech.transcript, speech.stop, speechToastId, onChange, value]);
  useEffect(() => () => {toast.dismiss(speechToastId);}, [speechToastId, speechContextKey]);
  const speechButtonClass = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40';
  const localInputRef = useRef<HTMLTextAreaElement>(null);
  const composerRef = inputRef ?? localInputRef;
  const queueing = running && queueWhileRunning && Boolean(value.trim());
  const mention = useBotMention(value, onChange, mentionBots);
  useLayoutEffect(() => {
    const input = composerRef.current;
    if (!input) return;
    resizeComposerInput(input);
  }, [value, composerRef]);
  useLayoutEffect(() => {
    const input = composerRef.current;
    if (!input) return;
    let previousWidth = input.getBoundingClientRect().width;
    const observer = new ResizeObserver(() => {
      const width = input.getBoundingClientRect().width;
      // Ignore the height notifications caused by resizing the editor itself.
      if (width === previousWidth) return;
      previousWidth = width;
      resizeComposerInput(input);
    });
    observer.observe(input);
    return () => observer.disconnect();
  }, [composerRef]);
  const hasText = Boolean(value.trim());
  const sendDisabled = disabled || !hasText || (running && !queueWhileRunning);
  const stopButton = <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 rounded-lg text-muted-foreground hover:bg-secondary"
    aria-label="Stop generating" title="Stop generating" disabled={!onStop} onClick={() => { speech.stop(); onStop?.(); }}><Square className="h-4 w-4" aria-hidden /></Button>;
  return <><SIBotMentionMenu mention={mention} /><QuantisAIComposerFrame
    accessibilityLabel={composerAriaLabel ?? `${assistantName} message composer`}
    input={<>
      {(speech.listening || speech.voiceMode || speech.speaking) && <QuantisAISpeechActivity
        phase={speech.listening ? 'listening' : speech.speaking ? 'speaking' : speech.awaitingReply || running ? 'waiting' : 'ready'}
        voice={speech.voiceMode} level={speech.inputLevel}
        onStop={speech.listening && !speech.voiceMode ? speech.toggleDictation : speech.stop} />}
      <textarea ref={composerRef} aria-label={`Message ${assistantName}`} name="quantis-ai-message" rows={1}
      placeholder={placeholder ?? `Ask ${assistantName}`} value={value} autoComplete="off" autoCorrect="on" autoCapitalize="sentences" spellCheck
      onChange={event => { speech.stop(); onChange(maxLength === undefined ? event.target.value : event.target.value.slice(0, maxLength)); }}
      onKeyDown={event => {
        if (mention.onKeyDown(event)) return;
        if (onKeyDown) { onKeyDown(event); return; }
        if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
          event.preventDefault();
          if (!sendDisabled && !speech.listening) { speech.stop(); onSend(); }
        }
      }}
      className="block w-full resize-none border-0 bg-transparent px-2 py-1.5 font-apple text-sm leading-5 text-foreground shadow-none outline-none placeholder:text-muted-foreground"
      style={{display: speech.listening || speech.voiceMode || speech.speaking ? 'none' : undefined, minHeight: QUANTIS_AI_COMPOSER_METRICS.inputMinHeight, maxHeight: QUANTIS_AI_COMPOSER_METRICS.inputMaxHeight, boxSizing: 'border-box'}} /></>}
    leading={<>
      <SIComposerTrailingControls running={running} model={modelPicker} takeoffModel={takeoffModelPicker} />
      {queueing && onSteer && <Button type="button" variant="ghost" size="sm" className="h-8 shrink-0 rounded-lg px-2 text-xs text-muted-foreground"
        disabled={disabled || speech.listening} onClick={() => { speech.stop(); onSteer(); }} title="Interrupt the current reply and send this message now">Steer</Button>}</>}
    trailing={<>{actions}
      {!speech.listening && <button type="button" aria-label={speech.listening && !speech.voiceMode ? 'Stop dictation' : 'Dictation'} aria-pressed={speech.listening && !speech.voiceMode}
        disabled={!speech.dictationSupported || disabled || running || speech.voiceMode}
        title={speech.dictationSupported ? 'Dictate into your draft. Your browser may process audio online.' : 'Dictation is not supported in this browser. Try Chrome or Safari.'}
        className={speechButtonClass} onClick={speech.toggleDictation}><Mic className="h-[18px] w-[18px]" aria-hidden /></button>}
{queueing && onStop && stopButton}</>}
    action={running && !queueing ? stopButton : !hasText && onVoiceSubmit ? <Button type="button" variant="ghost" size="icon"
      aria-label="Voice conversation" aria-pressed={speech.voiceMode}
      disabled={!speech.voiceSupported || disabled || speech.listening}
      title={speech.voiceSupported ? 'Speak a request and hear the reply. Your browser may process audio online.' : 'Voice needs browser speech recognition and speech playback. Try Chrome or Safari.'}
      className="h-8 w-8 shrink-0 rounded-full bg-secondary text-foreground hover:bg-accent disabled:opacity-40" onClick={speech.toggleVoice}>
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-[18px] w-[18px]" aria-hidden="true"><path d="M3 8v4m3-7v10m4-12v14m4-11v8m3-5v4" /></svg>
    </Button> : <Button type="button" variant="ghost" size="icon"
      className="h-8 w-8 shrink-0 rounded-full bg-brand text-primary-foreground hover:bg-brand/90 disabled:bg-secondary disabled:text-muted-foreground disabled:opacity-100"
      aria-label={sendAriaLabel ?? `Send to ${assistantName}`} disabled={sendDisabled || speech.listening} onClick={() => { speech.stop(); onSend(); }}>
      <ArrowUp className="h-4 w-4" aria-hidden />
    </Button>}
  /></>;
}
